import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Card, CardBody, Divider, Flex, Heading, Text, Button, Skeleton,
  CreateToastFnReturn, Link,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { generatePath, Link as RouterLink } from "react-router";
import TransparentCard from "components/Card/TransparentCard";
import { IntegrationRow, SlackLogo, ClaudeLogo } from "components/Integrations";
import { Text14 } from "components/Text";
import { API_ROUTES, API_ROUTES_V1, PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useCurrentUserQuery, userKeys } from "features/User/hooks";
import useDeferredApiCall from "hooks/useDeferredApiCall";
import { useNavbar } from "hooks/useNavbar";
import AppPage from "layouts/AppPage";
import ROLE from "models/role";
import { useCallback, useEffect, useState } from "react";
import apiClient from "services/ApiClient";
import { useToast } from "@chakra-ui/react";
import useCaminoStore from "hooks/useCaminoStore";

interface McpToken {
  id: number;
  name: string;
  createdAt: string;
  expiresAt: string | null;
}

export default function IndividualPage() {
  const { data: currentUser, isLoading } = useCurrentUserQuery({
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const { setBackLocationPath, setBackLocationName, setPageTitle } = useNavbar();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Match AuthenticatedRoute's fail-secure stance: only treat the session as
  // work_email when explicitly confirmed. Hides authorization controls (Slack OAuth,
  // MCP tokens) from pre-start (personal-email) sessions.
  const credentialType = useCaminoStore((state) => state.credentialType);
  const showSettings = credentialType === "work_email";
  const isPeopleTeamOrAdmin = currentUser?.permission?.includes("people_team" as ROLE)
    || currentUser?.permission?.includes("company_admin" as ROLE);
  const canConnectClaude = isPeopleTeamOrAdmin
    || currentUser?.permission?.includes("it_admin" as ROLE);

  useEffect(() => {
    setPageTitle("Individual Settings");
    return () => {
      setPageTitle(null);
      setBackLocationPath(null);
      setBackLocationName(null);
    };
  }, [setBackLocationName, setBackLocationPath, setPageTitle]);

  const { data: slackUrl } = useQuery<{ url: string }>({
    queryKey: ["slack-oauth-url"],
    queryFn: () => apiClient.get(API_ROUTES_V1.oauth.slack.individual.url),
    refetchOnWindowFocus: false,
  });

  const redirectToOauth = useCallback(() => {
    window.open(slackUrl?.url, "_blank", "rel=noopener noreferrer");
  }, [slackUrl]);

  const { performRequest } = useDeferredApiCall(generatePath(API_ROUTES_V1.oauth.slack.individual.base), {
    method: "DELETE",
    onSuccess: (_first, qc, t) => {
      qc.invalidateQueries({ queryKey: userKeys.current() });
      t({ title: "Successfully removed sending messages authorization", status: "success" });
    },
    onFailure: (_second, _third, t: CreateToastFnReturn) => {
      t({ title: "There was an error, please try again", status: "error" });
    },
  });

  const { data: mcpTokens = [] } = useQuery<McpToken[]>({
    queryKey: ["mcp-tokens"],
    queryFn: () => apiClient.get(API_ROUTES.mcpTokens.base),
  });

  const [revokingId, setRevokingId] = useState<number | null>(null);
  const { mutate: revokeMcpToken } = useMutation({
    mutationFn: (tokenId: number) =>
      apiClient.delete(generatePath(API_ROUTES.mcpTokens.show, { id: String(tokenId) })),
    onSuccess: () => {
      setRevokingId(null);
      queryClient.invalidateQueries({ queryKey: ["mcp-tokens"] });
      toast({ title: "Claude disconnected", status: "success" });
    },
    onError: () => {
      setRevokingId(null);
      toast({ title: "There was an error, please try again", status: "error" });
    },
  });

  const claudeConnectUrl = `https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=Camino&connectorUrl=${encodeURIComponent(`${window.location.origin}${API_ROUTES.mcp.sse}`)}`;

  return (
    <AppPage>
      <Heading mb="8" size="sm" color="fg.subtle">
        Individual Settings
      </Heading>
      {showSettings && isPeopleTeamOrAdmin && (
        <TransparentCard>
          <CardBody>
            <Text14>
              These settings are for your user account. If you&apos;d like to update the company settings, please visit
              {" "}
              <Link as={RouterLink} to={PEOPLE_TEAM_ROUTES.company.settings} color="brand.500" textDecoration="underline" _hover={{ textDecoration: "underline" }}>
                the company settings page
              </Link>
              .
            </Text14>
          </CardBody>
        </TransparentCard>
      )}
      {credentialType === null ? (
        <Card mt="8">
          <CardBody>
            <Skeleton height="100px" />
          </CardBody>
        </Card>
      ) : showSettings ? (
        <Card mt="8">
          <Skeleton isLoaded={!isLoading}>
            <CardBody>
              <Heading as="h3" color="fg.muted" size="xs" mb="5">
                Personal Integrations
              </Heading>

              <IntegrationRow
                logo={<SlackLogo />}
                name="Slack"
                description="Send Slack messages from Camino on your behalf"
                status={
                  currentUser?.sendMessageAuthorized
                    ? <CheckIcon color="green.500" flexShrink={0} aria-label="Authorized" />
                    : <CloseIcon color="red.400" boxSize="2.5" flexShrink={0} aria-label="Not authorized" />
                }
                action={
                  currentUser?.sendMessageAuthorized
                    ? <Button size="sm" variant="outline" onClick={performRequest} flexShrink={0}>Remove</Button>
                    : <Button size="sm" onClick={redirectToOauth} flexShrink={0}>Authorize</Button>
                }
              />

              {canConnectClaude && (
                <>
                  <Divider my="4" />

                  <IntegrationRow
                    logo={<ClaudeLogo />}
                    name="Claude"
                    description="Manage onboarding directly from your Claude conversations"
                    action={
                      <Button
                        as="a"
                        href={claudeConnectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        flexShrink={0}
                      >
                        Connect
                      </Button>
                    }
                  >
                    {mcpTokens.length > 0 && (
                      <Flex direction="column" gap="2">
                        {mcpTokens.map((token) => (
                          <Flex key={token.id} align="center" gap="2">
                            <Text fontSize="xs" color="fg.subtle">
                              {token.name} connected {format(new Date(token.createdAt), "MMM d, yyyy")}
                            </Text>
                            <CheckIcon color="green.500" boxSize="3" flexShrink={0} />
                            <Button
                              size="xs"
                              variant="outline"
                              flexShrink={0}
                              ml="auto"
                              isLoading={revokingId === token.id}
                              onClick={() => { setRevokingId(token.id); revokeMcpToken(token.id); }}
                            >
                              Disconnect
                            </Button>
                          </Flex>
                        ))}
                      </Flex>
                    )}
                  </IntegrationRow>
                </>
              )}
            </CardBody>
          </Skeleton>
        </Card>
      ) : (
        <Card mt="8">
          <CardBody>
            <Text color="fg.subtle">
              Account settings will be available once your work email is set up.
            </Text>
          </CardBody>
        </Card>
      )}
    </AppPage>
  );
}
